import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FlagDirective } from '@cf/features/i18n';
import { DropdownComponent, DropdownItemComponent } from '@cf/shared/dropdown';
import {
  FormFieldComponent,
  InputDirective,
  InputPrefixDirective,
  LabelComponent,
} from '@cf/shared/forms/form-field';
import { CfIconDirective } from '@cf/shared/ui/icons';
import { TextComponent } from '@cf/shared/ui/typography';
import { MaskitoDirective } from '@maskito/angular';
import type { MaskitoOptions } from '@maskito/core';
import { maskitoGetCountryFromNumber, maskitoPhoneOptionsGenerator } from '@maskito/phone';
import { NgxTolgeeModule } from '@tolgee/ngx';
import metadata from 'libphonenumber-js/min/metadata';
import { NgxControlValueAccessor } from 'ngxtension/control-value-accessor';
import { COUNTRIES, DEFAULT_COUNTRY } from '../const';
import { IPhoneCountryInterface } from '../interfaces';

@Component({
  selector: 'cf-form-phone-number',
  hostDirectives: [NgxControlValueAccessor],
  imports: [
    CommonModule,
    FormsModule,
    LabelComponent,
    ReactiveFormsModule,
    InputDirective,
    FormFieldComponent,
    InputPrefixDirective,
    NgxTolgeeModule,
    DropdownComponent,
    DropdownItemComponent,
    TextComponent,
    MaskitoDirective,
    FlagDirective,
    CfIconDirective,
  ],
  templateUrl: './form-phone-number.component.html',
  styleUrl: './form-phone-number.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormPhoneNumberComponent {
  label = input<string>();
  required = input<boolean>();

  countries = COUNTRIES;
  selectedCountry = DEFAULT_COUNTRY;
  filteredCountries = this.countries;

  phoneNumber = '';
  searchText = '';

  mask: Required<MaskitoOptions> = this.generateMask();

  private cva = inject<NgxControlValueAccessor<string>>(NgxControlValueAccessor);
  private cd = inject(ChangeDetectorRef);

  constructor() {
    this.phoneNumber = this.selectedCountry.dialCode;
    this.setupReactiveUpdates();
  }

  /**
   * Updates the mask options dynamically based on the selected country code.
   */
  private generateMask(): Required<MaskitoOptions> {
    return maskitoPhoneOptionsGenerator({
      metadata,
      strict: false,
      countryIsoCode: DEFAULT_COUNTRY.code,
    });
  }

  /**
   * Reactive listener for Control Value Accessor (CVA) value changes.
   */
  private setupReactiveUpdates(): void {
    effect(() => {
      const cvaValue = this.cva.value$();
      if (cvaValue) {
        this.phoneNumber = cvaValue;
        this.syncSelectedCountry();
      }
      this.cd.detectChanges();
    });
  }

  /**
   * Syncs the selected country based on the phone number.
   */
  private syncSelectedCountry(): void {
    const countryCode = maskitoGetCountryFromNumber(this.phoneNumber, metadata);
    if (countryCode) {
      const matchedCountry = this.countries.find((country) => country.code === countryCode);
      if (matchedCountry) {
        this.selectedCountry = matchedCountry;
      }
    }
  }

  /**
   * Handles country selection from the dropdown.
   */
  onCountrySelect(country: IPhoneCountryInterface): void {
    this.selectedCountry = country;
    this.phoneNumber = country.dialCode;
    this.emitPhoneNumber();
  }

  /**
   * Updates the phone number and emits the new value to the form control.
   */
  onPhoneInput(value: string): void {
    this.phoneNumber = value;
    this.syncSelectedCountry();
    this.emitPhoneNumber();
  }

  /**
   * Retrieve the pasted content.
   */
  onPaste(event: ClipboardEvent): void {
    const pastedText = event.clipboardData?.getData('text');
    if (pastedText) {
      this.phoneNumber = pastedText.charAt(0) === '+' ? pastedText : '+' + pastedText;
      this.onPhoneInput(this.phoneNumber);
      event.preventDefault();
    }
  }

  /**
   * Filters the countries list based on the search text.
   */
  filterCountries(): void {
    const search = this.searchText.toLowerCase();
    this.filteredCountries = this.countries.filter(
      (country) => country.name.toLowerCase().includes(search) || country.dialCode.includes(search),
    );
  }

  /**
   * Emits the sanitized phone number to the Control Value Accessor.
   */
  private emitPhoneNumber(): void {
    const sanitizedPhoneNumber = this.phoneNumber.replace(/[^\d+]/g, '');
    this.cva.value$.set(sanitizedPhoneNumber);
    this.cva.markAsTouched();
  }
}
